// ------------------------------------------------------------
// @project Pagali - Plataforma de Pagamentos
// @file Scoring.tsx
// @author Arlinda Peixoto
// @company Pagali
// @description Scoring de Crédito — distribuição de risco, top clientes
// @date 2026-04-22
// ------------------------------------------------------------

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid2 as Grid,
  Chip,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  LinearProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  TrendingUp,
  Star,
  Warning,
  CheckCircle,
  Refresh,
  BarChart,
} from '@mui/icons-material';
import DataTable, { Column, CurrencyCell } from '../../components/Common/DataTable';
import { useTranslation } from 'react-i18next';
import bankingService from '../../services/banking.service';

// ─── Tipos ───────────────────────────────────────────────────────────────────

interface ClientScore extends Record<string, any> {
  clientId: number;
  clientName: string;
  email: string;
  kycLevel: string;
  score: number;
  grade: string;
  gradeLabel: string;
  maxLoanAmount: number;
  autoApprove: boolean;
  isMock: boolean;
}

interface ScoreDistribution {
  grade: string;
  count: number;
  percentage: number;
}

interface ScoreStats {
  total: number;
  avgScore: number;
  autoApproveEligible: number;
  distribution: ScoreDistribution[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const GRADE_COLORS: Record<string, 'success' | 'info' | 'warning' | 'error' | 'default'> = {
  A: 'success',
  B: 'info',
  C: 'warning',
  D: 'error',
  E: 'error',
};

const GRADE_BG: Record<string, string> = {
  A: '#2e7d32',
  B: '#1565c0',
  C: '#f57f17',
  D: '#bf360c',
  E: '#4a148c',
};

function ScoreBar({ score }: { score: number }) {
  const pct = ((score - 300) / 550) * 100;
  const color = score >= 700 ? '#2e7d32' : score >= 600 ? '#1565c0' : score >= 500 ? '#f57f17' : '#bf360c';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flex: 1, bgcolor: 'action.selected', borderRadius: 1, height: 8 }}>
        <Box sx={{ width: `${pct}%`, bgcolor: color, height: '100%', borderRadius: 1, transition: 'width 0.4s' }} />
      </Box>
      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 36, color }}>
        {score}
      </Typography>
    </Box>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function Scoring() {
  const { t } = useTranslation();

  const [scores, setScores] = useState<ClientScore[]>([]);
  const [stats, setStats] = useState<ScoreStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bankingDisabled, setBankingDisabled] = useState(false);
  const [gradeFilter, setGradeFilter] = useState('');

  const fetchScores = useCallback(async () => {
    setLoading(true);
    setError(null);
    setBankingDisabled(false);
    try {
      const res = await bankingService.getCreditScores({
        grade: gradeFilter || undefined,
        limit: 100,
      });
      if (res?.bankingHubDisabled) {
        setBankingDisabled(true);
      } else if (res?.success) {
        setScores(res.data?.clients ?? []);
        setStats(res.data?.stats ?? null);
      } else {
        setError(res?.error ?? 'Erro desconhecido');
      }
    } catch (e: any) {
      setError(e.message ?? 'Erro ao carregar scores');
    } finally {
      setLoading(false);
    }
  }, [gradeFilter]);

  useEffect(() => { fetchScores(); }, [fetchScores]);

  // ── Stats cards ─────────────────────────────────────────────────────────────

  const statCards = stats ? [
    {
      label: 'Total Clientes',
      value: stats.total,
      icon: <BarChart />,
      color: '#7c4dff',
    },
    {
      label: 'Score Médio',
      value: stats.avgScore,
      icon: <TrendingUp />,
      color: '#1565c0',
    },
    {
      label: 'Auto-aprovação',
      value: stats.autoApproveEligible,
      icon: <CheckCircle />,
      color: '#2e7d32',
      subtitle: stats.total > 0 ? `${Math.round((stats.autoApproveEligible / stats.total) * 100)}% do total` : '',
    },
    {
      label: 'Risco Alto (D/E)',
      value: (stats.distribution.find(d => d.grade === 'D')?.count ?? 0) +
             (stats.distribution.find(d => d.grade === 'E')?.count ?? 0),
      icon: <Warning />,
      color: '#bf360c',
    },
  ] : [];

  // ── Colunas da tabela ────────────────────────────────────────────────────────

  const columns: Column<ClientScore>[] = [
    {
      id: 'clientName',
      label: 'Cliente',
      minWidth: 180,
      format: (_, row) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{row.clientName}</Typography>
          <Typography variant="caption" color="text.secondary">{row.email}</Typography>
        </Box>
      ),
    },
    {
      id: 'score',
      label: 'Score',
      minWidth: 160,
      format: (_, row) => <ScoreBar score={row.score} />,
    },
    {
      id: 'grade',
      label: 'Grade',
      minWidth: 100,
      format: (_, row) => (
        <Chip
          label={`${row.grade} — ${row.gradeLabel}`}
          color={GRADE_COLORS[row.grade] ?? 'default'}
          size="small"
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      id: 'kycLevel',
      label: 'KYC',
      minWidth: 110,
      format: (v) => (
        <Chip
          label={v as string}
          color={v === 'ENHANCED' ? 'success' : v === 'STANDARD' ? 'info' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      id: 'maxLoanAmount',
      label: 'Limite Máximo',
      minWidth: 130,
      format: (v) => <CurrencyCell value={v as number} currency="CVE" />,
    },
    {
      id: 'autoApprove',
      label: 'Auto-aprovação',
      minWidth: 120,
      format: (v) => (
        v
          ? <Chip label="Sim" color="success" size="small" icon={<CheckCircle />} />
          : <Chip label="Não" color="default" size="small" variant="outlined" />
      ),
    },
  ];

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Star sx={{ color: '#7c4dff', fontSize: 28 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Credit Scoring</Typography>
            <Typography variant="body2" color="text.secondary">
              Perfis de risco e pontuação de crédito dos clientes
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Atualizar">
          <IconButton onClick={fetchScores} disabled={loading}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {bankingDisabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Banking Hub desativado — aguarda autorização do Banco Central de Cabo Verde para ativação em produção.
        </Alert>
      )}
      {error && !bankingDisabled && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {bankingDisabled ? null : (<>
      {/* Stats cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((card) => (
          <Grid key={card.label} size={{ xs: 12, sm: 6, md: 3 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">{card.label}</Typography>
                  <Box sx={{ color: card.color }}>{card.icon}</Box>
                </Box>
                <Typography variant="h4" fontWeight={700} sx={{ color: card.color, mt: 0.5 }}>
                  {loading ? <CircularProgress size={24} /> : card.value}
                </Typography>
                {card.subtitle && (
                  <Typography variant="caption" color="text.secondary">{card.subtitle}</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Distribuição de grades */}
      {stats && stats.distribution.length > 0 && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Distribuição de Risco
            </Typography>
            <Grid container spacing={2}>
              {stats.distribution.map((d) => (
                <Grid key={d.grade} size={{ xs: 12, sm: 6, md: 'auto' }} sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: `${GRADE_BG[d.grade]}18`,
                      border: `1px solid ${GRADE_BG[d.grade]}40`,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="h4" fontWeight={800} sx={{ color: GRADE_BG[d.grade] }}>
                      {d.grade}
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>{d.count}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {d.percentage.toFixed(1)}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={d.percentage}
                      sx={{
                        mt: 1,
                        height: 6,
                        borderRadius: 3,
                        bgcolor: `${GRADE_BG[d.grade]}22`,
                        '& .MuiLinearProgress-bar': { bgcolor: GRADE_BG[d.grade] },
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Filtros */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 2 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Filtrar por Grade"
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                {['A', 'B', 'C', 'D', 'E'].map((g) => (
                  <MenuItem key={g} value={g}>
                    Grade {g}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                A calcular scores…
              </Typography>
            </Box>
          ) : (
            <DataTable
              columns={columns}
              data={scores}
              loading={false}
              emptyMessage="Nenhum cliente encontrado"
            />
          )}
        </CardContent>
      </Card>
      </>)}
    </Box>
  );
}
